// FirewallDhcpLeases.tsx
import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, Search, Wifi,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, Network, Clock
} from "lucide-react";
import { ItemsPerPageDropdown } from "../../../../../components/ItemsPerPageDropdown ";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";


// ── Types ─────────────────────────────────────────────────────────────────────
interface DhcpLeaseItem {
    id: number;
    created: string;
    updated: string;
    asset_id: number;
    firewall_type: string;
    ip: string;
    mac: string;
    hostname: string;
    status: string;
    interface: string;
    type: string;
    expire_time: number;
    reserved: boolean;
    vci: string | null;
    server_mkey: number;
    server_ipam_enabled: boolean;
    device_vendor: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtExpiry = (unix: number): { label: string; isExpired: boolean; isSoon: boolean } => {
    const now = Math.floor(Date.now() / 1000);
    const diff = unix - now;
    const isExpired = diff <= 0;
    const isSoon = diff > 0 && diff < 3600;

    if (isExpired) return { label: "Expired", isExpired: true, isSoon: false };

    const d = Math.floor(diff / 86400);
    const h = Math.floor((diff % 86400) / 3600);
    const m = Math.floor((diff % 3600) / 60);

    const parts = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);

    return { label: parts.join(" ") || "< 1m", isExpired: false, isSoon };
};

// ── Sort Icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ column, sortConfig }: {
    column: string;
    sortConfig: { key: string; direction: "asc" | "desc" } | null;
}) => {
    if (sortConfig?.key !== column) return (
        <div className="opacity-0 group-hover:opacity-40 transition-opacity">
            <ArrowUp size={10} className="text-slate-400" />
        </div>
    );
    return sortConfig.direction === "asc"
        ? <ArrowUp size={10} className="text-indigo-600 dark:text-indigo-400" />
        : <ArrowDown size={10} className="text-indigo-600 dark:text-indigo-400" />;
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallDhcpLeases({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [items, setItems] = useState<DhcpLeaseItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            if (debouncedSearch) queryObj.$or = [
                { ip: { $ilike: `%${debouncedSearch}%` } },
                { mac: { $ilike: `%${debouncedSearch}%` } },
                { hostname: { $ilike: `%${debouncedSearch}%` } },
                { interface: { $ilike: `%${debouncedSearch}%` } },
            ];
            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit,
            };
            if (sortConfig) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.direction; }
            const res = await baseapi.get("/firewall_dhcp_leases", { params });
            if (res.data.status) {
                setItems(res.data.data);
                setTotalRecords(res.data.total ?? res.data.data.length);
            } else showError("Failed to load DHCP leases");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, debouncedSearch, currentPage, limit, sortConfig]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSort = (key: string) => {
        setSortConfig(prev =>
            prev?.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    const Th = ({ label, col, right = false }: { label: string; col: string; right?: boolean }) => (
        <th
            className={`px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap ${right ? "text-right" : ""}`}
            onClick={() => handleSort(col)}
        >
            <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
                {label} <SortIcon column={col} sortConfig={sortConfig} />
            </div>
        </th>
    );

    return (
        <div className="flex flex-col bg-transparent text-slate-800 dark:text-gray-200">

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-indigo-100 dark:bg-indigo-600/20 p-1.5 rounded-lg border border-indigo-200 dark:border-indigo-500/30">
                        <Wifi size={16} className="text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <h1 className="text-lg font-bold text-slate-900 dark:text-white tracking-wide">DHCP Leases</h1>
                    <span className="text-[10px] font-bold text-slate-600 dark:text-gray-400 bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 px-2 py-0.5 rounded">
                        {totalRecords} leases
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-60 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-gray-500 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search IP, MAC, hostname..."
                            className="w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-all shadow-sm"
                        />
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-gray-700 transition-all group shrink-0 shadow-sm"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-indigo-600 dark:group-hover:text-indigo-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-slate-200 dark:border-gray-700/50">
                                <Th label="Hostname" col="hostname" />
                                <Th label="IP Address" col="ip" />
                                <Th label="MAC Address" col="mac" />
                                <Th label="Vendor" col="device_vendor" />
                                <Th label="Interface" col="interface" />
                                <Th label="Type" col="type" />
                                <Th label="Status" col="status" />
                                <Th label="Reserved" col="reserved" />
                                <Th label="Expires In" col="expire_time" right />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-gray-400">
                                            <Loader2 className="w-4 h-4 animate-spin text-indigo-600 dark:text-indigo-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600 dark:text-gray-300">Loading DHCP leases...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-60">
                                            <Wifi size={24} className="text-slate-400 dark:text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">No DHCP leases found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                items.map((item) => {
                                    const expiry = fmtExpiry(item.expire_time);
                                    return (
                                        <tr key={item.id} className="hover:bg-slate-50/80 dark:hover:bg-gray-700/30 transition-colors">

                                            {/* Hostname */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[11px] font-bold text-slate-900 dark:text-gray-100">
                                                        {item.hostname || <span className="text-slate-400 dark:text-gray-600">—</span>}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* IP Address */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="font-mono text-[12px] font-bold text-cyan-700 dark:text-cyan-300">
                                                    {item.ip}
                                                </span>
                                            </td>

                                            {/* MAC Address */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="font-mono text-[11px] text-amber-700 dark:text-yellow-300/80 tracking-wide uppercase">
                                                    {item.mac}
                                                </span>
                                            </td>

                                            {/* Vendor */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="font-mono text-[11px] text-slate-700 dark:text-gray-200 tracking-wide uppercase">
                                                    {item.device_vendor || "—"}
                                                </span>
                                            </td>

                                            {/* Interface */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border bg-slate-100 dark:bg-gray-700/50 border-slate-200 dark:border-gray-600/40 text-slate-700 dark:text-gray-300">
                                                    <Network size={9} />
                                                    {item.interface}
                                                </span>
                                            </td>

                                            {/* Type */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase ${item.type === "ipv4"
                                                    ? "bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/20 text-blue-700 dark:text-blue-400"
                                                    : "bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/20 text-purple-700 dark:text-purple-400"
                                                    }`}>
                                                    {item.type}
                                                </span>
                                            </td>

                                            {/* Status */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase ${item.status === "leased"
                                                    ? "bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/20 text-green-700 dark:text-green-400"
                                                    : item.status === "expired"
                                                        ? "bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400"
                                                        : "bg-slate-100 dark:bg-gray-500/10 border-slate-200 dark:border-gray-500/20 text-slate-600 dark:text-gray-400"
                                                    }`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${item.status === "leased" ? "bg-green-500 dark:bg-green-400 animate-pulse"
                                                        : item.status === "expired" ? "bg-red-500 dark:bg-red-400"
                                                            : "bg-slate-400 dark:bg-gray-400"
                                                        }`} />
                                                    {item.status}
                                                </span>
                                            </td>

                                            {/* Reserved */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border ${item.reserved
                                                    ? "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20 text-amber-700 dark:text-amber-400"
                                                    : "bg-slate-100 dark:bg-gray-700/40 border-slate-200 dark:border-gray-600/30 text-slate-500 dark:text-gray-500"
                                                    }`}>
                                                    {item.reserved ? "Reserved" : "Dynamic"}
                                                </span>
                                            </td>

                                            {/* Expires In */}
                                            <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                                <span className={`inline-flex items-center gap-1 justify-end font-mono text-[11px] font-bold ${expiry.isExpired ? "text-red-600 dark:text-red-400"
                                                    : expiry.isSoon ? "text-amber-600 dark:text-yellow-400"
                                                        : "text-slate-700 dark:text-gray-300"
                                                    }`}>
                                                    <Clock size={10} className={
                                                        expiry.isExpired ? "text-red-500"
                                                            : expiry.isSoon ? "text-amber-500 dark:text-yellow-500"
                                                                : "text-slate-400 dark:text-gray-600"
                                                    } />
                                                    {expiry.label}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 dark:bg-gray-800/40 border-t border-slate-200 dark:border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-slate-500 dark:text-gray-400 tracking-widest">
                            <span className="text-slate-700 dark:text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-slate-700 dark:text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-slate-400 dark:text-gray-600">/</span>
                            <span className="text-slate-800 dark:text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button onClick={() => setSearchTerm("")}
                                className="text-[9px] text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-bold uppercase tracking-widest transition-colors">
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-indigo-700 dark:text-indigo-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
